/**
 * Работы графика у позиции сметы (СМ-16). Связь та же, что в карточке работы графика (ПЛ-01).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Input } from '../../ui/form';

type ActivityLink = {
  id: string;
  scheduleId: string;
  code: string;
  name: string;
  unit: string | null;
  quantity: number | null;
};

export function ItemScheduleLinks({
  estimateId,
  itemId,
  projectId,
  canEdit,
}: {
  estimateId: string;
  itemId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const list = useQuery({
    queryKey: ['estimate', estimateId, 'item', itemId, 'activities'],
    queryFn: () => api<ActivityLink[]>(`/estimates/${estimateId}/items/${itemId}/activities`),
  });
  const search = useQuery({
    queryKey: ['estimate', estimateId, 'schedule-activities', q],
    queryFn: () =>
      api<ActivityLink[]>(`/estimates/${estimateId}/schedule-activities?q=${encodeURIComponent(q.trim())}`),
    enabled: canEdit && q.trim().length >= 2,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['estimate', estimateId, 'item', itemId, 'activities'] });
  };

  const post = async (activityId: string, remove?: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/estimates/${estimateId}/items/${itemId}/activities`, {
        method: 'POST',
        body: { activityId, remove: remove || undefined },
      });
      setQ('');
      refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  if (list.isPending) return <Spinner label="Работы графика" />;
  if (list.isError) return <ErrorBox error={list.error} />;

  const linked = new Set(list.data.map((a) => a.id));
  const found = (search.data ?? []).filter((a) => !linked.has(a.id));

  return (
    <div className="flex flex-col gap-2 border-t border-line/60 pt-3">
      <h4 className="text-[13px] font-semibold text-ink">Работы графика</h4>
      {!list.data.length ? (
        <p className="text-[12px] text-ink-2">С позицией пока не связаны работы графика.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {list.data.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
              <Link
                to="/schedule/$projectId"
                params={{ projectId }}
                className="min-h-[44px] text-accent-text underline md:min-h-[28px]"
              >
                {a.code} · {a.name}
              </Link>
              {canEdit ? (
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center text-[12px] text-ink-2 underline md:min-h-[28px]"
                  disabled={busy}
                  onClick={() => void post(a.id, true)}
                >
                  отвязать
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        <div className="flex flex-col gap-2">
          <Input
            className="!h-11"
            value={q}
            placeholder="Найти работу: код или название"
            aria-label="Найти работу графика"
            onChange={(e) => setQ(e.target.value)}
          />
          {search.isFetching ? <p className="text-[12px] text-ink-2">Ищем…</p> : null}
          {found.length ? (
            <ul className="flex flex-col gap-1">
              {found.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                  <span>
                    {a.code} · {a.name}
                  </span>
                  <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center text-[12px] text-accent-text underline md:min-h-[28px]"
                    disabled={busy}
                    onClick={() => void post(a.id)}
                  >
                    связать
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}
